import { useUserContext } from 'lib/context'
import { ContentChartType, ISO8601DateTimeString, Mall, MetricsChartType } from 'lib/dto'
import { HostType } from 'configs/theme'

export type Block =
  | 'main_screen'
  | 'content'
  | 'availability'
  | 'placement'
  | 'lost_profit'
  | 'warehouses'
  | 'reviews'
  | 'reviews_by_country'
  | 'product_charts'
  | 'variants'
  | 'variants_and_offers'
  | 'description_versions'
  | 'external_advertising'

export type MallConfig = {
  excludeCharts?: Set<MetricsChartType | ContentChartType>
  excludeBlocks?: Set<Block>
  idsAreMeaningful: boolean
  mallFirstObserved: ISO8601DateTimeString
}

type MallConfigLocal = {
  excludeCharts?: readonly (MetricsChartType | ContentChartType)[]
  excludeBlocks?: readonly Block[]
  idsAreMeaningful: boolean
  mallFirstObserved: ISO8601DateTimeString
}

const _mallConfig = {
  ali_tmall: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'variants_and_offers',
      'warehouses',
      'description_versions',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2019-04-19T18:04:18.000000+03:00',
  },
  amazon_ae: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2023-02-07T23:59:59.000000+03:00',
  },
  apteka: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-07-23T00:10:04.000000+03:00',
  },
  beru: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-07-03T01:10:03.000000+03:00',
  },
  citilink: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-06-21T01:10:11.000000+03:00',
  },
  decathlon: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'warehouses',
      'variants_and_offers',
      'description_versions',
      'external_advertising',
    ],
    idsAreMeaningful: false,
    mallFirstObserved: '2022-02-17T14:14:12.000000+03:00',
  },
  detmir: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-12-16T00:10:20.000000+03:00',
  },
  dns: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-06-17T01:10:11.000000+03:00',
  },
  eapteka: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'description_versions',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-07-25T13:05:13.000000+03:00',
  },
  eldorado: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: false,
    mallFirstObserved: '2020-07-29T16:14:15.000000+03:00',
  },
  goldapple: {
    excludeCharts: ['rating', 'orders'],
    excludeBlocks: ['reviews_by_country', 'variants_and_offers', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2023-08-17T00:00:00.000000+03:00',
  },
  goods: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'warehouses',
      'description_versions',
      'variants',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-07-06T18:19:25.000000+03:00',
  },
  holodilnik: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: false,
    mallFirstObserved: '2020-07-12T00:10:02.000000+03:00',
  },
  komus: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'warehouses',
      'variants_and_offers',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-02-11T00:10:45.000000+03:00',
  },
  lamoda: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2019-06-18T18:46:35.000000+03:00',
  },
  lavka: {
    excludeCharts: ['rating', 'orders', 'mallReviews', 'review'],
    excludeBlocks: [
      'reviews',
      'reviews_by_country',
      'warehouses',
      'description_versions',
      'variants',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-01-17T12:29:23.000000+03:00',
  },
  lenta: {
    excludeCharts: ['orders', 'gmv', 'sales', 'inventory'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-01-17T12:56:21.000000+03:00',
  },
  magnit_market: {
    excludeCharts: ['rating', 'orders', 'mallReviews', 'review'],
    excludeBlocks: [
      'reviews',
      'reviews_by_country',
      'warehouses',
      'variants',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2023-09-20T12:29:23.000000+03:00',
  },
  magnit_cosmetic: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2023-09-20T12:29:23.000000+03:00',
  },
  mvideo: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-01-12T15:57:49.000000+03:00',
  },
  nana: {
    excludeCharts: ['orders', 'rating', 'review', 'mallReviews'],
    excludeBlocks: [
      'description_versions',
      'warehouses',
      'reviews',
      'reviews_by_country',
      'variants',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-10-28T14:49:28.000000+03:00',
  },
  noon: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-08-02T11:10:43.000000+03:00',
  },
  noon_grocery: {
    excludeCharts: ['gmv', 'sales', 'rating', 'orders', 'mallReviews', 'review'],
    excludeBlocks: [
      'reviews_by_country',
      'warehouses',
      'reviews',
      'variants',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-09-24T00:10:32.000000+03:00',
  },
  noon_saudi: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-10-06T19:05:38.000000+03:00',
  },
  onlinetrade: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-06-27T11:09:30.000000+03:00',
  },
  ozon: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'variants_and_offers', 'variants'],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-05-31T00:00:00.000000+03:00',
  },
  ozon_fresh: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2023-09-27T00:00:00.000000+03:00',
  },
  ozon2: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'variants_and_offers', 'variants'],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-05-31T00:00:00.000000+03:00',
  },
  perekrestok: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-02-02T00:10:31.000000+03:00',
  },
  samokat: {
    excludeCharts: ['mallReviews', 'review', 'orders', 'rating'],
    excludeBlocks: [
      'reviews',
      'reviews_by_country',
      'warehouses',
      'variants',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2021-12-15T00:18:26.000000+03:00',
  },
  sbermarket: {
    excludeCharts: ['mallReviews', 'review', 'orders', 'rating'],
    excludeBlocks: [
      'reviews',
      'reviews_by_country',
      'warehouses',
      'description_versions',
      'variants',
      'external_advertising',
    ],
    idsAreMeaningful: false,
    mallFirstObserved: '2021-10-19T14:04:53.000000+03:00',
  },
  shopify: {
    excludeCharts: ['rating', 'orders'],
    excludeBlocks: [
      'placement',
      'reviews_by_country',
      'warehouses',
      'variants_and_offers',
      'description_versions',
    ],
    idsAreMeaningful: false,
    mallFirstObserved: '2022-01-01T23:59:59.000000+03:00',
  },
  talabat: {
    excludeCharts: ['orders', 'rating', 'review', 'mallReviews'],
    excludeBlocks: [
      'warehouses',
      'reviews',
      'reviews_by_country',
      'variants',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2022-11-03T18:44:09.000000+03:00',
  },
  toy: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'description_versions',
    ],
    idsAreMeaningful: false,
    mallFirstObserved: '2020-12-19T00:10:08.000000+03:00',
  },
  utkonos: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'description_versions',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2020-07-27T20:24:32.000000+03:00',
  },
  uzum: {
    excludeCharts: ['orders'],
    excludeBlocks: ['variants_and_offers', 'warehouses', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2023-11-24T00:00:00.000000+03:00',
  },
  wb: {
    excludeCharts: ['orders'],
    excludeBlocks: ['variants_and_offers'],
    idsAreMeaningful: true,
    mallFirstObserved: '2019-05-24T00:00:00.000000+03:00',
  },
  vprok: {
    excludeCharts: ['orders'],
    excludeBlocks: ['reviews_by_country', 'warehouses', 'variants', 'external_advertising'],
    idsAreMeaningful: true,
    mallFirstObserved: '2021-11-18T17:01:04.000000+03:00',
  },
  zalando: {
    excludeCharts: ['gmv', 'sales', 'inventory', 'orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'description_versions',
      'external_advertising',
    ],
    idsAreMeaningful: true,
    mallFirstObserved: '2021-11-05T00:10:04.000000+03:00',
  },
  zdravcity: {
    excludeCharts: ['orders'],
    excludeBlocks: [
      'reviews_by_country',
      'variants_and_offers',
      'warehouses',
      'external_advertising',
    ],
    idsAreMeaningful: false,
    mallFirstObserved: '2020-07-26T13:47:18.000000+03:00',
  },
} satisfies Record<string, MallConfigLocal>

export type MallCode = keyof typeof _mallConfig

const withSets = (config: Record<string, MallConfigLocal>): Record<string, MallConfig> => {
  const result = { ...config } as Record<string, MallConfig>
  for (const key of Object.keys(config)) {
    const { excludeCharts, excludeBlocks, ...rest } = config[key]
    result[key] = {
      ...rest,
      excludeCharts: new Set(excludeCharts),
      excludeBlocks: new Set(excludeBlocks),
    }
  }

  return result
}

export const mallConfig = withSets(_mallConfig)

class MallConfigObject {
  constructor(
    readonly currentMall: Mall,
    readonly hostType: HostType
  ) {}

  hasChart(chart: MetricsChartType | ContentChartType) {
    if (
      chart === 'lostProfit' &&
      (mallConfig[this.currentMall.code]?.excludeCharts?.has('gmv') ||
        mallConfig[this.currentMall.code]?.excludeCharts?.has('sales'))
    ) {
      return false
    }

    return !mallConfig[this.currentMall.code]?.excludeCharts?.has(chart)
  }

  hasBlock(block: Block) {
    if (
      block === 'warehouses' &&
      this.currentMall.code === 'ozon' &&
      this.hostType !== 'sellematics'
    ) {
      return false
    }

    if (
      block === 'lost_profit' &&
      (mallConfig[this.currentMall.code]?.excludeCharts?.has('gmv') ||
        mallConfig[this.currentMall.code]?.excludeCharts?.has('sales'))
    ) {
      return false
    }

    return !mallConfig[this.currentMall.code]?.excludeBlocks?.has(block)
  }

  get hasFeedRankings() {
    return ['wb', 'ozon'].includes(this.currentMall.code)
  }
  get hasMeaningfulIds() {
    return mallConfig[this.currentMall.code].idsAreMeaningful
  }
  get hasPriceVariation() {
    return !(<MallCode[]>[
      'apteka',
      'citilink',
      'eldorado',
      'holodilnik',
      'lamoda',
      'mvideo',
      'onlinetrade',
      'ozon',
      'toy',
      'utkonos',
      'wb',
      'zdravcity',
    ]).includes(this.currentMall.code)
  }
  get firstObserved() {
    return mallConfig[this.currentMall.code].mallFirstObserved
  }
}

export const useMallConfig = () => {
  const { currentMall, theme } = useUserContext()

  return new MallConfigObject(currentMall, theme.hostType)
}
